const responseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(`API request failed (${status})`);
  }
}

const transientReadErrors = new Set(['tick_pending', 'tick_processing']);
const transientReadRetryDelaysMs = [150, 300, 600, 1_200, 2_400] as const;

const errorCode = (body: unknown): string | null => typeof body === 'object'
  && body !== null
  && 'error' in body
  && typeof body.error === 'string'
  ? body.error
  : null;

const wait = (milliseconds: number): Promise<void> => new Promise((resolve) => {
  globalThis.setTimeout(resolve, milliseconds);
});

const cookieValue = (name: string): string | null => {
  const prefix = `${encodeURIComponent(name)}=`;
  const value = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
  return value ? decodeURIComponent(value.slice(prefix.length)) : null;
};

export const api = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const method = (init?.method ?? 'GET').toUpperCase();
  const csrfToken = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    ? cookieValue('businesia_csrf')
    : null;
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        ...init?.headers
      }
    });
    const body = await responseBody(response);
    const retryDelay = transientReadRetryDelaysMs[attempt];
    if (
      method === 'GET'
      && response.status === 409
      && transientReadErrors.has(errorCode(body) ?? '')
      && retryDelay !== undefined
    ) {
      await wait(retryDelay);
      continue;
    }
    if (!response.ok) throw new ApiError(response.status, body);
    return body as T;
  }
};
